import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning } from "./HeroCard";
import { effectiveQuality } from "./quality";
import { describeReadingState, describeThreshold } from "./readingMeaning";

interface ValueHeroWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  editable?: boolean;
  onConfigure?: () => void;
  onGenerateChart?: () => void;
}

/**
 * Cartão herói de VALOR numérico (`v`/`acc`/calc): o número é o herói; quando
 * está tudo bem e fresco, o cartão fica QUIETO (sem linha de significado). Os
 * estados de ausência (aguardando / sem leitura recente / offline) falam em
 * sentence case — substitui o `NumberWidget` e o `SilentWidget` legados, que
 * gritavam "DADO DESATUALIZADO / ÚLTIMA LEITURA RECEBIDA…" em caixa alta.
 */
export function ValueHeroWidget({
  widget,
  value,
  editable,
  onConfigure,
  onGenerateChart,
}: ValueHeroWidgetProps) {
  const decimals = widget.settings.decimals ?? 0;
  const unit = widget.settings.unit?.trim() || null;
  const sourceLabel = formatPublicSourceLabel(widget.source, widget.settings.signalKind);
  const caption = sourceLabel !== widget.title ? sourceLabel : null;
  const customUnavailable = widget.settings.stateLabels?.unavailable;

  const available = value.isAvailable && effectiveQuality(value) === "live";
  const meaning = describeReadingState(value, customUnavailable) ?? describeThreshold(widget, value);
  const cardTone: "ok" | "warn" | "danger" =
    meaning?.tone === "danger"
      ? "danger"
      : meaning?.tone === "warn"
        ? "warn"
        : "ok";
  // Rodapé: fresco → "atualizado há Xs"; desatualizado → idade da última
  // leitura (informativa); aguardando/offline → silêncio.
  const footnote = available
    ? `Atualizado ${formatWidgetRelative(value.measuredAt) ?? "agora"}`
    : value.measuredAt
      ? `Última leitura ${formatWidgetRelative(value.measuredAt)}`
      : null;

  return (
    <HeroCard
      title={widget.title}
      caption={caption}
      tone={cardTone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footnote={footnote}
    >
      <div className="w-hero__value">
        {available ? formatWidgetNumber(value.numericValue, decimals) : "--"}
        {unit ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? (
        <HeroMeaning tone={meaning.tone} animated={meaning.animated}>
          {meaning.text}
        </HeroMeaning>
      ) : null}
    </HeroCard>
  );
}
